<div class="dg">
  <!-- Table wrapper -->
  <div class="dg__body">

    @if (loading) {
      <div class="dg__overlay" aria-live="polite" aria-label="Carregando">
        <span class="dg__spinner" aria-hidden="true"></span>
        <span>Carregando…</span>
      </div>
    }

    @if (error) {
      <div class="dg__error">
        <i class="bi bi-exclamation-circle-fill" aria-hidden="true"></i>
        {{ error }}
      </div>
    }

    @if (!error) {
      @if (getMobileSortableColumns().length > 0) {
        <div class="dg__mobile-sort">
          <label class="dg__mobile-sort-label" for="dg-mobile-sort-{{ path }}">Ordenar por</label>
          <select
            class="dg__mobile-sort-select"
            id="dg-mobile-sort-{{ path }}"
            [ngModel]="getMobileSortField()"
            (ngModelChange)="changeMobileSort($event)"
          >
            @for (col of getMobileSortableColumns(); track col.field) {
              <option [value]="col.valuePath ?? col.field">{{ col.header }}</option>
            }
          </select>
          <button
            type="button"
            class="dg__mobile-sort-direction"
            [attr.aria-label]="getMobileSortDirection() === 'asc' ? 'Ordenação crescente' : 'Ordenação decrescente'"
            (click)="toggleMobileSortDirection()"
          >
            <i
              class="bi"
              [class.bi-sort-alpha-down]="getMobileSortDirection() === 'asc'"
              [class.bi-sort-alpha-up]="getMobileSortDirection() === 'desc'"
              aria-hidden="true"
            ></i>
            <span>{{ getMobileSortDirection() === 'asc' ? 'Asc' : 'Desc' }}</span>
          </button>
        </div>
      }

      <table class="dg__table">
        <thead>
          <tr>
            @for (col of columns; track col.field) {
              <th
                class="dg__th"
                [class.dg__th--sortable]="col.sortable"
                (click)="col.sortable ? changeSort(col) : null"
                [attr.aria-sort]="getSortMode(col) ?? 'none'"
              >
                <span class="dg__th-inner">
                  {{ col.header }}
                  @if (col.sortable) {
                    @switch (getSortMode(col)) {
                      @case ('asc')  { <i class="bi bi-chevron-up dg__sort-icon dg__sort-icon--active" aria-hidden="true"></i> }
                      @case ('desc') { <i class="bi bi-chevron-down dg__sort-icon dg__sort-icon--active" aria-hidden="true"></i> }
                      @default       { <i class="bi bi-chevron-expand dg__sort-icon" aria-hidden="true"></i> }
                    }
                  }
                </span>
              </th>
            }
            @if (hasActions()) {
              <th class="dg__th dg__th--actions">Ações</th>
            }
          </tr>
        </thead>
        <tbody>
          @for (row of data; track getRowId(row)) {
            <tr
              class="dg__tr"
              [class.dg__tr--clickable]="usesEditEmitter()"
              (click)="usesEditEmitter() ? onEdit(row, $event) : null"
            >
              @for (col of columns; track col.field) {
                <td class="dg__td" [class]="col.cellClass ?? ''">
                  @if (isInlineField(col)) {
                    @if (isEditingCell(row, col)) {
                      <input
                        class="dg__inline-input"
                        type="number"
                        [(ngModel)]="editingValue"
                        (blur)="commitInlineEdit(row, col)"
                        (keydown.enter)="commitInlineEdit(row, col)"
                        (keydown.escape)="cancelInlineEdit()"
                        (click)="$event.stopPropagation()"
                        #inlineInput
                        autofocus
                      />
                    } @else {
                      <span
                        class="dg__inline-trigger"
                        title="Clique para editar"
                        (click)="startInlineEdit(row, col, $event)"
                      >{{ formatValue(getValue(row, col), col) }}</span>
                    }
                  } @else if (col.format === 'badge') {
                    <span class="dg__badge" [attr.data-status]="getValue(row, col)">
                      {{ formatValue(getValue(row, col), col) }}
                    </span>
                  } @else {
                    {{ formatValue(getValue(row, col), col) }}
                  }
                </td>
              }
              @if (hasActions()) {
                <td class="dg__td dg__td--actions">
                  <div class="dg__actions">
                    @for (act of customActions; track act.id) {
                      <button
                        type="button"
                        class="dg__btn"
                        [class]="act.class || ''"
                        [title]="act.title"
                        [attr.aria-label]="act.title || act.id"
                        (click)="onCustomAction(act.id, row, $event)"
                      >
                        <i [class]="'bi ' + act.icon" aria-hidden="true"></i>
                      </button>
                    }
                    @if (usesEditRoute() && hasEditAction()) {
                      <a
                        [routerLink]="[editRoutePrefix, getRowId(row), 'edit']"
                        class="dg__btn dg__btn--edit"
                        title="Editar"
                        aria-label="Editar"
                        (click)="$event.stopPropagation()"
                      >
                        <i class="bi bi-pencil" aria-hidden="true"></i>
                      </a>
                    } @else if (usesEditEmitter()) {
                      <button
                        type="button"
                        class="dg__btn dg__btn--edit"
                        title="Editar"
                        aria-label="Editar"
                        (click)="onEdit(row, $event)"
                      >
                        <i class="bi bi-pencil" aria-hidden="true"></i>
                      </button>
                    }
                    @if (hasDeleteAction()) {
                      <button
                        type="button"
                        class="dg__btn dg__btn--delete"
                        title="Excluir"
                        aria-label="Excluir"
                        (click)="onDelete(row, $event)"
                      >
                        <i class="bi bi-trash" aria-hidden="true"></i>
                      </button>
                    }
                  </div>
                </td>
              }
            </tr>
          }
        </tbody>
      </table>

      <div class="dg__cards">
        @for (row of data; track getRowId(row)) {
          <article
            class="dg__card"
            [class.dg__card--clickable]="usesEditEmitter()"
            (click)="usesEditEmitter() ? onEdit(row, $event) : null"
          >
            <div class="dg__card-header">
              <div class="dg__card-heading">
                @if (getMobileTitleColumn(); as titleCol) {
                  <span class="dg__card-kicker">{{ getMobileLabel(titleCol) }}</span>
                  <strong class="dg__card-title">{{ formatValue(getValue(row, titleCol), titleCol) }}</strong>
                }
              </div>
              @if (getMobileBadgeColumn(); as badgeCol) {
                <span class="dg__badge dg__card-badge" [attr.data-status]="getValue(row, badgeCol)">
                  {{ formatValue(getValue(row, badgeCol), badgeCol) }}
                </span>
              }
            </div>

            @if (getMobileFieldColumns().length > 0) {
              <dl class="dg__card-fields">
                @for (col of getMobileFieldColumns(); track col.field) {
                  <div class="dg__card-field" [class]="col.cellClass ?? ''">
                    <dt class="dg__card-label">{{ getMobileLabel(col) }}</dt>
                    <dd class="dg__card-value">
                      @if (isInlineField(col)) {
                        @if (isEditingCell(row, col)) {
                          <input
                            class="dg__inline-input"
                            type="number"
                            [(ngModel)]="editingValue"
                            (blur)="commitInlineEdit(row, col)"
                            (keydown.enter)="commitInlineEdit(row, col)"
                            (keydown.escape)="cancelInlineEdit()"
                            (click)="$event.stopPropagation()"
                            autofocus
                          />
                        } @else {
                          <span
                            class="dg__inline-trigger"
                            title="Clique para editar"
                            (click)="startInlineEdit(row, col, $event)"
                          >{{ formatValue(getValue(row, col), col) }}</span>
                        }
                      } @else if (col.format === 'badge') {
                        <span class="dg__badge" [attr.data-status]="getValue(row, col)">
                          {{ formatValue(getValue(row, col), col) }}
                        </span>
                      } @else {
                        {{ formatValue(getValue(row, col), col) }}
                      }
                    </dd>
                  </div>
                }
              </dl>
            }

            @if (hasActions()) {
              <div class="dg__card-actions">
                @for (act of customActions; track act.id) {
                  <button
                    type="button"
                    class="dg__btn"
                    [class]="act.class || ''"
                    [title]="act.title"
                    [attr.aria-label]="act.title || act.id"
                    (click)="onCustomAction(act.id, row, $event)"
                  >
                    <i [class]="'bi ' + act.icon" aria-hidden="true"></i>
                  </button>
                }
                @if (usesEditRoute() && hasEditAction()) {
                  <a
                    [routerLink]="[editRoutePrefix, getRowId(row), 'edit']"
                    class="dg__btn dg__btn--edit"
                    title="Editar"
                    aria-label="Editar"
                    (click)="$event.stopPropagation()"
                  >
                    <i class="bi bi-pencil" aria-hidden="true"></i>
                  </a>
                } @else if (usesEditEmitter()) {
                  <button
                    type="button"
                    class="dg__btn dg__btn--edit"
                    title="Editar"
                    aria-label="Editar"
                    (click)="onEdit(row, $event)"
                  >
                    <i class="bi bi-pencil" aria-hidden="true"></i>
                  </button>
                }
                @if (hasDeleteAction()) {
                  <button
                    type="button"
                    class="dg__btn dg__btn--delete"
                    title="Excluir"
                    aria-label="Excluir"
                    (click)="onDelete(row, $event)"
                  >
                    <i class="bi bi-trash" aria-hidden="true"></i>
                  </button>
                }
              </div>
            }
          </article>
        }
      </div>

      @if (!loading && data.length === 0) {
        <div class="dg__empty">
          <i class="bi bi-inbox dg__empty-icon" aria-hidden="true"></i>
          <span>{{ emptyMessage }}</span>
        </div>
      }
    }
  </div>

  <!-- Footer: paginator -->
  @if (totalElements > 0) {
    <div class="dg__footer">
      <app-paginator
        [totalRecords]="totalElements"
        [recordsPerPage]="pageSize"
        [page]="page"
        (pageChange)="onPageChange($event)"
        (recordsPerPageChange)="onRecordsPerPageChange($event)"
      />
    </div>
  }
</div>
